import { createSignal } from 'solid-js';
import type { IconName } from './ui';

/**
 * The admin app's pages (ADR-0150, ADR-0154, ADR-0161, ADR-0223), in the rail's order, each under its group: a page and an
 * argument live in the address (`#players/<id>`, `#skins/gun:inferno`), so the back button and a pasted link work.
 */
export const PAGES = {
  players: {
    group: 'Players',
    title: 'Players',
    blurb: 'Find an account; ban it, make it a partner or an admin',
    icon: 'users',
  },
  newcomers: {
    group: 'Players',
    title: 'Newcomers',
    blurb: 'Watch new players’ first games, played by the build that played them',
    icon: 'eye',
  },
  reports: {
    group: 'Players',
    title: 'Reports',
    blurb: 'What players reported: read it, dismiss it or ban, within a day',
    icon: 'flag',
  },
  creators: {
    group: 'Players',
    title: 'Creators',
    blurb: 'Verified streamers: the purple check and /c/ links',
    icon: 'twitch',
  },
  population: { group: 'Game', title: 'Population', blurb: 'The bots, and the crowd PLAY shows', icon: 'activity' },
  skins: {
    group: 'Game',
    title: 'Skins',
    blurb: 'See every skin on a gardener; release those in preview, at a price',
    icon: 'sparkles',
  },
  maps: {
    group: 'Game',
    title: 'Maps',
    blurb: 'Draw on a map what you don’t like and say why; clanker fixes it (the map-notes skill)',
    icon: 'map',
  },
  tournament: { group: 'Game', title: 'Tournament', blurb: 'The preseason bracket, region by region', icon: 'trophy' },
  codes: { group: 'Growth', title: 'Access codes', blurb: 'The codes and links that bring new accounts', icon: 'key' },
  shorts: {
    group: 'Growth',
    title: 'Shorts',
    blurb: 'Approve or deny each short; the server posts the approved',
    icon: 'film',
  },
} as const satisfies Record<string, { group: string; title: string; blurb: string; icon: IconName }>;
export type Page = keyof typeof PAGES;
export const PAGE_KEYS = Object.keys(PAGES) as Page[];
/** The rail's groups, in order, with their pages. */
export const GROUPS = [...new Set(PAGE_KEYS.map((p) => PAGES[p].group))].map(
  (group) => [group, PAGE_KEYS.filter((p) => PAGES[p].group === group)] as const,
);

export interface Route {
  page: Page;
  /** What on the page (a player's id, a skin's key, `new`), or ''. */
  arg: string;
}

const parse = (hash: string): Route => {
  const [page = '', ...rest] = hash.replace(/^#/, '').split('/');
  return Object.hasOwn(PAGES, page)
    ? { page: page as Page, arg: decodeURIComponent(rest.join('/')) }
    : { page: 'players', arg: '' };
};

const [route, setRoute] = createSignal(parse(location.hash));
addEventListener('hashchange', () => setRoute(parse(location.hash)));

export { route };

/** Opens `page` (on `arg`): a step in the browser's history. */
export function go(page: Page, arg = ''): void {
  location.hash = arg ? `${page}/${encodeURIComponent(arg)}` : page;
}
